// X(pbs.twimg.com)のプロフィール画像を Referer なしで取得し、blob URL に変える。
// pbs.twimg.com は X 以外の Referer 付きリクエストを 351 で拒否する。vis-network は内部の new Image() で
// 画像を読み、referrerPolicy を渡す口が無いので、先に fetch(referrerPolicy: "no-referrer")で取って blob URL を渡す。
// テストできるように fetch・createObjectURL・revokeObjectURL・setTimeout・clearTimeout・signal は引数で受け取る。

export const PROFILE_IMAGE_FETCH_TIMEOUT_MS = 8000
export const NODE_IMAGE_UPDATE_DELAY_MS = 150

// http(s) の URL だけ取得する。TwUser#profile_image_url は空文字を返しうる
export function isFetchableImageUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url)
}

// 「画像 URL → その画像を使うノード ID の配列」。取得できない URL は含めない
export function groupNodeIdsByImageUrl(items, { idKey = "id", urlKey = "profile_img" } = {}) {
  const idsByUrl = new Map()
  ;(items || []).forEach((item) => {
    const url = item?.[urlKey]
    if (!isFetchableImageUrl(url)) return

    if (!idsByUrl.has(url)) idsByUrl.set(url, [])
    idsByUrl.get(url).push(item[idKey])
  })
  return idsByUrl
}

// 画像の取得器を作る。load(url) は blob URL(取得できなければ null)で解決する Promise を返す。
// - 同じ URL は 1 回だけ取得する
// - ok でない応答・ネットワーク失敗・CORS 失敗・タイムアウトは null(呼び出し側は既定アイコンのままにする)
// - 親の signal が中断済みなら、作った blob URL をその場で revoke して null を返す
// - revokeAll() で、この取得器が作った blob URL だけを revoke する
export function createProfileImageLoader({
  fetch,
  createObjectURL,
  revokeObjectURL,
  setTimeout,
  clearTimeout,
  signal,
  timeoutMs = PROFILE_IMAGE_FETCH_TIMEOUT_MS
}) {
  const requests = new Map()
  const createdUrls = new Set()

  async function fetchAsBlobUrl(url) {
    if (signal?.aborted) return null

    // AbortSignal.any / AbortSignal.timeout は古いブラウザに無いので、子の AbortController を
    // タイマーと親の abort イベントの両方から止める
    const controller = new AbortController()
    const abortChild = () => controller.abort()
    signal?.addEventListener("abort", abortChild, { once: true })
    const timer = setTimeout(abortChild, timeoutMs)

    try {
      const response = await fetch(url, { referrerPolicy: "no-referrer", signal: controller.signal })
      if (!response.ok) return null

      const blob = await response.blob()
      const blobUrl = createObjectURL(blob)
      if (signal?.aborted || controller.signal.aborted) {
        revokeObjectURL(blobUrl)
        return null
      }

      createdUrls.add(blobUrl)
      return blobUrl
    } catch {
      return null
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener("abort", abortChild)
    }
  }

  function load(url) {
    if (!isFetchableImageUrl(url)) return Promise.resolve(null)

    if (!requests.has(url)) requests.set(url, fetchAsBlobUrl(url))
    return requests.get(url)
  }

  function revokeAll() {
    createdUrls.forEach((blobUrl) => revokeObjectURL(blobUrl))
    createdUrls.clear()
  }

  return { load, revokeAll }
}

// push した項目をためておき、最後の push から delayMs 後にまとめて 1 回 apply する(デバウンス)。
// vis-network の nodes.update は呼ぶたびに物理シミュレーションを再起動するため。
export function createBatchedUpdater({ apply, setTimeout, clearTimeout, delayMs = NODE_IMAGE_UPDATE_DELAY_MS }) {
  let pending = []
  let timer = null

  function flush() {
    timer = null
    if (pending.length === 0) return

    const items = pending
    pending = []
    apply(items)
  }

  function push(items) {
    pending.push(...items)
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(flush, delayMs)
  }

  function cancel() {
    if (timer !== null) clearTimeout(timer)
    timer = null
    pending = []
  }

  return { push, cancel }
}
